import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Phone, FileText, Plus, Save } from 'lucide-react';

import { Card, Badge, Button, Input, DataTable, Modal } from '../../../components/ui';
import { useAuth } from '../../../app/auth';
import { creditoService } from '../../../services/creditoService';
import { fetchAPI } from '../../../services/apiClient';
import { Cliente, ResumenCreditoLocal } from '../../../types';

interface VendedorOption {
  id: string;
  nombre: string;
}

interface CrearClienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  isLocal: boolean;
  localId?: string;
  vendedores: VendedorOption[];
}

interface ConfigFormState {
  max_creditos: string;
  max_monto_total: string;
}

const CrearClienteModal: React.FC<CrearClienteModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  isLocal,
  localId,
  vendedores,
}) => {
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setNombre('');
    setTelefono('');
    setError('');
    setVendedorId(vendedores[0]?.id || '');
  }, [isOpen, vendedores]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      setError('Nombre es obligatorio');
      return;
    }
    if (!localId) {
      setError('No hay local asignado');
      return;
    }
    if (isLocal && !vendedorId) {
      setError('Seleccione un vendedor');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await creditoService.crearCliente({
        nombre: nombre.trim(),
        telefono: telefono.trim() || undefined,
        local_id: localId,
        vendedor_id: isLocal ? vendedorId : undefined,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al crear cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nuevo Cliente" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nombre *"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Nombre del cliente"
          autoFocus
        />
        <Input
          label="Teléfono"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
          placeholder="8888-8888"
        />
        {isLocal && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor *</label>
            <select
              value={vendedorId}
              onChange={(e) => setVendedorId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="">Seleccione un vendedor</option>
              {vendedores.map((vendedor) => (
                <option key={vendedor.id} value={vendedor.id}>{vendedor.nombre}</option>
              ))}
            </select>
          </div>
        )}
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose} className="flex-1">
            Cancelar
          </Button>
          <Button variant="primary" type="submit" isLoading={saving} className="flex-1">
            Crear
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export const VendedorCreditosPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLocal = user?.role === 'LOCAL';

  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [error, setError] = useState('');
  const [resumenLocal, setResumenLocal] = useState<ResumenCreditoLocal | null>(null);
  const [vendedores, setVendedores] = useState<VendedorOption[]>([]);
  const [configForms, setConfigForms] = useState<Record<string, ConfigFormState>>({});
  const [savingConfigId, setSavingConfigId] = useState<string | null>(null);

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    try {
      const clientesData = await creditoService.listarClientes();
      setClientes(clientesData);

      if (isLocal) {
        const [resumen, vendedoresData] = await Promise.all([
          creditoService.obtenerResumenLocal(),
          fetchAPI<VendedorOption[]>('/usuarios/local/vendedores'),
        ]);
        setResumenLocal(resumen);
        setVendedores(vendedoresData.map((item: any) => ({ id: item.id, nombre: item.nombre })));

        const nextForms: Record<string, ConfigFormState> = {};
        resumen.vendedores.forEach((item) => {
          nextForms[item.vendedor_id] = {
            max_creditos: String(item.max_creditos || 0),
            max_monto_total: String(item.max_monto_total || 0),
          };
        });
        setConfigForms(nextForms);
      }
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar los créditos');
      setClientes([]);
      setResumenLocal(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [isLocal]);

  const getSaldo = (cliente: Cliente) => Math.round(Number(cliente.saldo_pendiente || cliente.saldoPendiente || 0));
  const fmt = (n: number) => `₡${n.toLocaleString('es-CR', { maximumFractionDigits: 0 })}`;

  const filteredClientes = useMemo(() => clientes.filter((cliente) => {
    if (!searchTerm) return true;
    const query = searchTerm.toLowerCase();
    return cliente.nombre.toLowerCase().includes(query) || (cliente.telefono || '').includes(searchTerm);
  }), [clientes, searchTerm]);

  const totalDeuda = useMemo(() => clientes.reduce((sum, cliente) => sum + getSaldo(cliente), 0), [clientes]);
  const clientesConDeuda = useMemo(() => clientes.filter((cliente) => getSaldo(cliente) > 0).length, [clientes]);

  const actualizarConfigForm = (vendedorId: string, campo: keyof ConfigFormState, valor: string) => {
    setConfigForms((prev) => ({
      ...prev,
      [vendedorId]: {
        ...(prev[vendedorId] || { max_creditos: '0', max_monto_total: '0' }),
        [campo]: valor,
      },
    }));
  };

  const guardarConfig = async (vendedorId: string) => {
    const config = configForms[vendedorId];
    const maxCreditos = Number(config?.max_creditos || 0);
    const maxMontoTotal = Number(config?.max_monto_total || 0);
    if (!Number.isFinite(maxCreditos) || maxCreditos <= 0 || !Number.isFinite(maxMontoTotal) || maxMontoTotal <= 0) {
      setError('Los límites deben ser mayores a cero');
      return;
    }

    setSavingConfigId(vendedorId);
    setError('');
    try {
      await creditoService.guardarConfigVendedor(vendedorId, {
        max_creditos: maxCreditos,
        max_monto_total: maxMontoTotal,
      });
      await cargarDatos();
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar la configuración');
    } finally {
      setSavingConfigId(null);
    }
  };

  const columns = [
    {
      key: 'nombre',
      header: 'Cliente',
      render: (cliente: Cliente) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{cliente.nombre}</p>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Phone size={12} />
              {cliente.telefono || '-'}
            </p>
          </div>
        </div>
      ),
    },
    ...(isLocal ? [{
      key: 'vendedor',
      header: 'Vendedor',
      render: (cliente: Cliente) => {
        const vendedor = resumenLocal?.vendedores.find((item) => item.vendedor_id === cliente.vendedor_id);
        return <span className="text-sm text-gray-700">{vendedor?.vendedor_nombre || 'Sin asignar'}</span>;
      },
    }] : []),
    {
      key: 'saldo',
      header: 'Saldo usado',
      render: (cliente: Cliente) => {
        const saldo = getSaldo(cliente);
        return (
          <span className={`font-bold ${saldo > 0 ? 'text-danger-600' : 'text-success-600'}`}>
            {fmt(saldo)}
          </span>
        );
      },
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (cliente: Cliente) => {
        if (cliente.bloqueado_credito) return <Badge variant="danger">Bloqueado</Badge>;
        if (cliente.activo === false) return <Badge variant="neutral">Inactivo</Badge>;
        return <Badge variant="success">Activo</Badge>;
      },
    },
    {
      key: 'acciones',
      header: '',
      render: (cliente: Cliente) => (
        <button
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/vendedor/creditos/${cliente.id}`);
          }}
          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
          title="Ver detalle"
        >
          <FileText size={18} />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Créditos</h1>
          <p className="text-gray-500">
            {isLocal ? 'Controla límites y exposición por vendedor' : 'Gestiona tus clientes y su exposición de crédito'}
          </p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={18} />} onClick={() => setShowCrearModal(true)}>
          Nuevo Cliente
        </Button>
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Clientes con deuda</p>
          <p className="text-2xl font-bold text-warning-600">{clientesConDeuda}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Deuda visible</p>
          <p className="text-2xl font-bold text-danger-600">{fmt(totalDeuda)}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Total del local</p>
          <p className="text-2xl font-bold text-primary-600">{fmt(resumenLocal?.total_credito_local || totalDeuda)}</p>
        </Card>
      </div>

      {isLocal && resumenLocal && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Control por vendedor</h2>
              <p className="text-sm text-gray-500">Configura cupos y monitorea el crédito usado en el local</p>
            </div>
          </div>

          <div className="space-y-4">
            {resumenLocal.vendedores.map((item) => (
              <div key={item.vendedor_id} className="border border-gray-200 rounded-xl p-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">{item.vendedor_nombre}</p>
                    <p className="text-sm text-gray-500">
                      {item.cantidad_creditos_activos} créditos activos · {fmt(item.saldo_total_activo)} usados
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:w-[560px]">
                    <Input
                      label="Máx. créditos"
                      type="number"
                      value={configForms[item.vendedor_id]?.max_creditos || ''}
                      onChange={(e) => actualizarConfigForm(item.vendedor_id, 'max_creditos', e.target.value)}
                    />
                    <Input
                      label="Máx. monto total"
                      type="number"
                      value={configForms[item.vendedor_id]?.max_monto_total || ''}
                      onChange={(e) => actualizarConfigForm(item.vendedor_id, 'max_monto_total', e.target.value)}
                    />
                    <div className="flex items-end">
                      <Button
                        variant="secondary"
                        leftIcon={<Save size={16} />}
                        onClick={() => guardarConfig(item.vendedor_id)}
                        isLoading={savingConfigId === item.vendedor_id}
                        className="w-full"
                      >
                        Guardar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-4">
          <Input
            placeholder="Buscar por nombre o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search size={18} />}
          />
        </div>
        <DataTable
          data={filteredClientes}
          columns={columns}
          keyExtractor={(cliente) => cliente.id}
          onRowClick={(cliente) => navigate(`/vendedor/creditos/${cliente.id}`)}
          isLoading={loading}
          emptyMessage="No hay clientes para mostrar"
        />
      </Card>

      <CrearClienteModal
        isOpen={showCrearModal}
        onClose={() => setShowCrearModal(false)}
        onCreated={cargarDatos}
        isLocal={isLocal}
        localId={user?.localId}
        vendedores={vendedores}
      />
    </div>
  );
};

export default VendedorCreditosPage;